import { gql, useLazyQuery } from '@apollo/client'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import LinearProgress from '@mui/material/LinearProgress'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { Query } from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import { useEffect, useMemo } from 'react'

import ChildSpacer from '@src/lib/ChildSpacer'

import InYourIndustry from './InYourIndustry'
import manageBrandsPaths from './manageBrandsPaths'

const GET_BRAND_REQUESTS = gql`
  query getBrandRequests($resource_type: String!, $resource_id: ID!) {
    getBrandRequests(resource_type: $resource_type, resource_id: $resource_id) {
      data {
        id
        status
        brand_name
        brand_logo_url
        brand_id
        brand {
          id
          name
          logo_url
        }
      }
    }
  }
`

const PendingBrandRequests: React.FC<React.PropsWithChildren> = () => {
  const params = manageBrandsPaths.index.useParams()

  const [runQuery, { data, loading, refetch, called }] = useLazyQuery<Query>(
    GET_BRAND_REQUESTS,
    { notifyOnNetworkStatusChange: true, fetchPolicy: 'network-only' },
  )
  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.retailerAccountSlug])

  const fetchData = () => {
    const variables = {
      resource_type: 'retailer_accounts',
      resource_id: params.retailerAccountSlug,
    }
    called ? refetch(variables) : runQuery({ variables })
  }

  const filteredResults = useMemo(() => {
    const filteredResults =
      data?.getBrandRequests?.data.filter((brandRequest) => {
        return (
          brandRequest.status === 'pending' &&
          (!!brandRequest.brand_name || !!brandRequest.brand?.name)
        )
      }) || []
    filteredResults.sort((a, b) => {
      const aName = a.brand?.name || a.brand_name
      const bName = b.brand?.name || b.brand_name
      if (aName && bName) {
        return aName.localeCompare(bName)
      }
      return 0
    })
    return filteredResults
  }, [data?.getBrandRequests?.data])

  return (
    <>
      <InYourIndustry
        isLoading={loading}
        onDidRequest={fetchData}
        // Intentionally passing ALL brand requests, rather than just pending.
        ignoreBrandIds={data?.getBrandRequests?.data.map((x) => x.brand_id)}
      />
      <br />
      <br />
      <Paper>
        <Box padding={4}>
          <Typography variant="h6">
            {I18n.t('manageBrands.pendingRequests')}
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>{I18n.t('manageBrands.brand')}</strong>
                </TableCell>
                <TableCell style={{ width: 100 }}>
                  <strong>{I18n.t('manageBrands.status')}</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && filteredResults.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2}>
                    <Box textAlign="center">
                      <Typography variant="h6" color="textSecondary">
                        {I18n.t('manageBrands.noBrandRequests')}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {filteredResults.map((brandRequest) => {
                const brandName =
                  brandRequest.brand?.name || brandRequest.brand_name
                const brandLogo =
                  brandRequest.brand?.logo_url || brandRequest.brand_logo_url
                return (
                  <TableRow key={brandRequest.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <ChildSpacer>
                          {brandLogo && (
                            <img
                              src={brandLogo}
                              alt={brandName || 'Logo'}
                              width={50}
                            />
                          )}
                          <ListItemText primary={brandName} />
                        </ChildSpacer>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={brandRequest.status.toUpperCase()}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {loading && <LinearProgress />}
      </Paper>
    </>
  )
}

export default PendingBrandRequests
